import { Phone } from "lucide-react";
import { Button } from "./ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";

const PRIMARY_CTA_SELECTOR = "[data-primary-cta]";
const FALLBACK_SCROLL_Y = 400;
// Lazy-loaded pages mount their hero after the route changes; poll briefly for the CTA.
const CTA_LOOKUP_RETRIES = 10;
const CTA_LOOKUP_INTERVAL_MS = 200;

const StickyMobileCTA = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);

  // Hide on admin pages and book-inspection page
  const hiddenPaths = ['/admin', '/book-inspection'];
  const shouldHide = hiddenPaths.some(path => location.pathname.startsWith(path));

  // Show the bar only while no inline primary CTA is on screen.
  // Fallback (page without a primary CTA): show after the user scrolls past 400px.
  useEffect(() => {
    if (shouldHide) return;

    let observer: IntersectionObserver | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let scrollHandler: (() => void) | null = null;
    setIsVisible(false);

    const observePrimaryCtas = (targets: Element[]) => {
      const onScreen = new Set<Element>();
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) onScreen.add(entry.target);
            else onScreen.delete(entry.target);
          });
          setIsVisible(onScreen.size === 0);
        },
        { threshold: 0 }
      );
      targets.forEach((el) => observer!.observe(el));
    };

    const startScrollFallback = () => {
      scrollHandler = () => setIsVisible(window.scrollY > FALLBACK_SCROLL_Y);
      window.addEventListener('scroll', scrollHandler, { passive: true });
      scrollHandler();
    };

    const findCtas = (attempt: number) => {
      const targets = Array.from(document.querySelectorAll(PRIMARY_CTA_SELECTOR));
      if (targets.length > 0) {
        observePrimaryCtas(targets);
      } else if (attempt < CTA_LOOKUP_RETRIES) {
        retryTimer = setTimeout(() => findCtas(attempt + 1), CTA_LOOKUP_INTERVAL_MS);
      } else {
        startScrollFallback();
      }
    };

    findCtas(0);

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      if (observer) observer.disconnect();
      if (scrollHandler) window.removeEventListener('scroll', scrollHandler);
    };
  }, [location.pathname, shouldHide]);

  if (shouldHide || !isVisible) return null;

  return (
    <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 glass border-t border-border shadow-[0_-4px_20px_-1px_rgba(0,0,0,0.12)] safe-area-bottom sticky-cta-enter tap-highlight-none">
      <div className="p-3">
      <div className="flex gap-2">
        <Button
          variant="cta"
          onClick={() => {
            // Track Meta Pixel Contact event
            const w = window as unknown as { fbq?: (...args: unknown[]) => void };
            if (typeof window !== 'undefined' && w.fbq) {
              w.fbq('track', 'Contact', {
                content_name: 'Get Free Inspection - Sticky Mobile CTA'
              });
            }
            navigate("/book-inspection");
          }}
          className="flex-1 h-12 font-semibold btn-press"
        >
          Get Free Inspection
        </Button>
        <a
          href="tel:+13462200989"
          className="flex h-12 min-w-12 items-center justify-center gap-2 px-4 bg-secondary text-white rounded-block font-semibold transition-all duration-200 active:scale-95 btn-press"
          aria-label="Call us at (346) 220-0989"
        >
          <Phone className="w-5 h-5" />
        </a>
      </div>
      </div>
    </div>
  );
};

export default StickyMobileCTA;
